const MAX_IMAGE_BYTES = 2 * 1024 * 1024
const MAX_IMAGE_SIDE = 1920
const JPEG_OPTIMIZE_THRESHOLD = 256 * 1024
const PNG_OPTIMIZE_THRESHOLD = 768 * 1024

const canvasBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Image could not be encoded.')), type, quality)
})

export const OptimizeBoqImageBlob = async (source, extension) => {
    if (typeof createImageBitmap !== 'function') return null

    const bitmap = await createImageBitmap(source)
    try {
        const longestSide = Math.max(bitmap.width, bitmap.height)
        let scale = Math.min(1, MAX_IMAGE_SIDE / longestSide)
        const threshold = extension === 'png' ? PNG_OPTIMIZE_THRESHOLD : JPEG_OPTIMIZE_THRESHOLD
        if (scale === 1 && source.size < threshold && source.size <= MAX_IMAGE_BYTES) return null

        const mimeType = extension === 'png' ? 'image/png' : 'image/jpeg'
        const qualities = extension === 'png' ? [undefined] : [0.84, 0.76, 0.68]
        let smallest = null
        const minimumScale = Math.min(scale, 0.25)

        while (scale >= minimumScale) {
            const canvas = document.createElement('canvas')
            canvas.width = Math.max(1, Math.round(bitmap.width * scale))
            canvas.height = Math.max(1, Math.round(bitmap.height * scale))
            const context = canvas.getContext('2d')
            if (!context) throw new Error('Image processing is unavailable in this browser.')
            context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)

            for (const quality of qualities) {
                const candidate = await canvasBlob(canvas, mimeType, quality)
                if (candidate.size < source.size && (!smallest || candidate.size < smallest.size)) smallest = candidate
                if (candidate.size <= MAX_IMAGE_BYTES) return candidate.size < source.size ? candidate : null
                if (source.size <= MAX_IMAGE_BYTES) break
            }
            if (source.size <= MAX_IMAGE_BYTES || scale <= minimumScale) break
            scale = Math.max(minimumScale, scale * 0.8)
        }

        return smallest
    } finally {
        bitmap.close?.()
    }
}

// Direct uploads use the same PNG/JPEG encoder as embedded Excel images.
// GIF and WebP retain their original bytes and remain subject to the size limit.
export const OptimizeBoqImageFile = async file => {
    const extension = file.type === 'image/png' ? 'png' : file.type === 'image/jpeg' ? 'jpeg' : null
    if (!extension || !file.size) return file
    const optimized = await OptimizeBoqImageBlob(file, extension)
    if (!optimized || optimized.size >= file.size) return file
    return new File([optimized], file.name, {type: file.type, lastModified: file.lastModified})
}
